import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { PermissionAccessProvider, PermissionAwareTextarea } from '../../../contexts/PermissionAccessContext';
import { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import { newChildRow } from './ticketForm';
import { CHILD_FIELDS, TSHIRT_OPTIONS, fromDateInput, gridOptions, liveRows as live, lookupOptions, ratingClass, rowKeyOf } from './unitForm';
import { Field, LookupSelectField, Section, SelectField, TextField, inputClass, str } from './unitFields';
import { fmtDateTime } from './safetyDrawerShared';
import { ChildListsProps, ChildrenStatus, UnitChildGrid } from './UnitChildGrid';
import SafetyTableDrawer from './SafetyTableDrawer';
import { tableComponentId } from './tableForm';

/**
 * Retention tab of the Unit drawer — V1 `@retention_layout`, three columns:
 * [Company Rep, Dispatcher, Surveys (last 5, "Show all")] | [Awards] |
 * [Bio, Internal Comments, Country, T-shirt size]. Scalars -> PUT /units/{r}/retention
 * (diff); awards -> PUT /units/{r}/children.
 *
 * Surveys are DISPLAY-ONLY here (Phase 5 Task 7): a row click / "Add survey" opens the
 * Safety Tables `survey` drawer, where the per-category elements are edited and the rating
 * is computed (V1 `editsurvey`). A new survey is created with the unit's driver fixed. The
 * drawer gates itself on `safety-system-table-survey`; the same level hides "Add survey".
 * After a save the drawer's owner re-reads the surveys list.
 */

export interface UnitRetentionTabProps extends ChildListsProps {
  retention: Record<string, any>;
  onRetentionChange: (key: string, value: unknown) => void;
  canEdit: boolean;
  fieldErrors: Record<string, string>;
  /** The unit's current driver (`detail.driver.recno`); null = no driver, no "Add survey". */
  driverrecno: number | null;
  /** Re-read the surveys list after the survey drawer saved. */
  onSurveysSaved: () => void | Promise<void>;
}

const RECENT_SURVEYS = 5;
const SURVEY_TABLE = 'survey';
const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'px-1.5 py-1 align-top';

export default function UnitRetentionTab({
  retention, onRetentionChange, canEdit, fieldErrors, driverrecno, onSurveysSaved,
  childRows, onChildRowsChange, lookups, childrenLoading, childrenError, rowErrors,
}: UnitRetentionTabProps) {
  const r = (key: string) => (v: unknown) => onRetentionChange(key, v);
  const gridsReady = !childrenLoading && !childrenError;
  const [showAll, setShowAll] = useState(false);
  const [surveyDrawer, setSurveyDrawer] = useState<number | 'new' | null>(null);
  const canAddSurvey = useResolvedAccessLevel(tableComponentId(SURVEY_TABLE), true) >= 2 && !!driverrecno;

  // Surveys arrive newest first; show the last five saved ones.
  const surveys = live(childRows.surveys ?? []).filter((row) => row.recno);
  const hidden = showAll ? 0 : Math.max(0, surveys.length - RECENT_SURVEYS);
  const visible = showAll ? surveys : surveys.slice(0, RECENT_SURVEYS);
  const typeLabel = (row: Record<string, any>) =>
    str(row.surveytypename) || lookups.surveytypes.find((t) => String(t.recno) === str(row.surveytyperecno))?.label || str(row.surveytyperecno);

  const handleSurveySaved = async () => {
    setSurveyDrawer(null);
    await onSurveysSaved();
  };

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* ---- Column 1: reps + surveys ---- */}
        <div className="space-y-3">
          <Section title="Retention">
            <TextField label="Company Rep" value={retention.companyrep} onChange={r('companyrep')} />
            <TextField label="Dispatcher" value={retention.dispatcher} onChange={r('dispatcher')} />
          </Section>
          {gridsReady ? (
            <fieldset className="rounded-md border border-rule p-2">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">Surveys</legend>
              <div className="space-y-2">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" aria-label="Surveys">
                    <thead>
                      <tr>
                        <th className={th}>#</th><th className={th}>Survey Type</th><th className={th}>Survey Date</th>
                        <th className={th}>Rating</th><th className={th}>Surveyed By</th><th className={th}>Survey Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.length === 0 && (
                        <tr><td colSpan={6} className="px-2 py-3 text-center text-sm text-muted">No surveys.</td></tr>
                      )}
                      {visible.map((row, i) => {
                        const n = i + 1;
                        const open = () => setSurveyDrawer(Number(row.recno));
                        return (
                          <tr key={rowKeyOf(row)} className="cursor-pointer border-t border-rule hover:bg-highlight" onClick={open}>
                            <td className={`${td} text-xs text-muted`}>
                              <button type="button" aria-label={`Open survey ${n}`} onClick={(e) => { e.stopPropagation(); open(); }}
                                className="text-xs font-medium text-accent-text hover:underline">
                                {n}
                              </button>
                            </td>
                            <td className={`${td} whitespace-nowrap`} aria-label={`Survey Type ${n}`}>{typeLabel(row)}</td>
                            <td className={`${td} whitespace-nowrap`} aria-label={`Survey Date ${n}`}>{fmtDateTime(row.surveydate)}</td>
                            <td className={`${td} font-semibold ${ratingClass(row.surveyrating)}`} aria-label={`Rating ${n}`}>{str(row.surveyrating)}</td>
                            <td className={`${td} whitespace-nowrap`} aria-label={`Surveyed By ${n}`}>{str(row.surveyedby)}</td>
                            <td className={`${td} max-w-[16rem] break-words`} aria-label={`Survey Comments ${n}`}>{str(row.surveycomments)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {surveys.length > RECENT_SURVEYS && (
                  <button type="button" onClick={() => setShowAll((v) => !v)} className="text-xs font-medium text-accent-text hover:underline">
                    {showAll ? `Show last ${RECENT_SURVEYS}` : `Show all (${hidden} more)`}
                  </button>
                )}
                {canAddSurvey && (
                  <button type="button" onClick={() => setSurveyDrawer('new')}
                    className="inline-flex items-center gap-1 rounded-md border border-rule px-2 py-1 text-sm hover:bg-highlight">
                    <PlusIcon width={14} aria-hidden="true" />Add survey
                  </button>
                )}
              </div>
            </fieldset>
          ) : <ChildrenStatus title="Surveys" loading={childrenLoading} error={childrenError} />}
        </div>

        {/* ---- Column 2: awards ---- */}
        <div className="space-y-3">
          {gridsReady ? (
            <UnitChildGrid
              title="Awards"
              rows={childRows.awards ?? []}
              onRowsChange={(rows) => onChildRowsChange('awards', rows)}
              columns={[
                { key: 'awardtype', label: 'Award Type', type: 'select', options: gridOptions(lookups.awardtypes), className: 'min-w-[10rem]' },
                { key: 'awarddate', label: 'Award Date', type: 'date' },
              ]}
              newRow={() => newChildRow(CHILD_FIELDS.awards, { awarddate: fromDateInput(new Date()) })}
              addLabel="Add award"
              removeLabel="Remove award"
              emptyText="No awards."
              errors={rowErrors.awards}
            />
          ) : <ChildrenStatus title="Awards" loading={childrenLoading} error={childrenError} />}
        </div>

        {/* ---- Column 3: bio ---- */}
        <div className="space-y-3">
          <Section title="Bio">
            <Field label="Bio" className="col-span-2">
              <PermissionAwareTextarea aria-label="Bio" className={`${inputClass} min-h-[6rem]`} value={str(retention.driverbio)}
                onChange={(e) => onRetentionChange('driverbio', e.target.value)} />
            </Field>
            <Field label="Internal Comments" className="col-span-2">
              <PermissionAwareTextarea aria-label="Internal Comments" className={`${inputClass} min-h-[6rem]`} value={str(retention.drivercomments)}
                onChange={(e) => onRetentionChange('drivercomments', e.target.value)} />
            </Field>
            <LookupSelectField label="Country" value={retention.countryoforigin} onChange={r('countryoforigin')} options={lookupOptions(lookups.countries)} search />
            <SelectField label="T-Shirt Size" value={retention.tshirtsize} onChange={r('tshirtsize')} options={TSHIRT_OPTIONS} />
            {Object.entries(fieldErrors).map(([field, message]) => (
              <p key={field} role="alert" className="col-span-2 text-xs text-danger-fg">{message}</p>
            ))}
          </Section>
        </div>
      </div>

      {surveyDrawer !== null && (
        <SafetyTableDrawer
          tableKey={SURVEY_TABLE}
          recno={surveyDrawer}
          onClose={() => setSurveyDrawer(null)}
          onSaved={handleSurveySaved}
          {...(surveyDrawer === 'new' && driverrecno ? { fixed: { driverrecno } } : {})}
        />
      )}
    </PermissionAccessProvider>
  );
}
